/** Chorus controls component — UI for the Juno-60 style chorus effect.
 *
 * Displays three mode buttons (I, II, I+II) and an enable/disable toggle,
 * matching the original Juno-60 panel layout.
 */

import { Waves } from "lucide-react";
import { useCallback } from "react";
import type { ChorusMode, ChorusParams } from "../lib/Chorus.ts";
import { CHORUS_MODE_LABELS, CHORUS_MODES } from "../lib/Chorus.ts";

/** Props for the ChorusControls component. */
export type ChorusControlsProps = {
	readonly params: ChorusParams;
	readonly onChange: (update: Partial<ChorusParams>) => void;
};

export function ChorusControls({ params, onChange }: ChorusControlsProps) {
	const handleModeChange = useCallback(
		(mode: ChorusMode) => {
			onChange({ mode });
		},
		[onChange],
	);

	return (
		<div className="chorus-controls">
			<div className="chorus-header">
				<div className="chorus-title">
					<Waves size={14} />
					<span>Chorus</span>
				</div>
				<button
					type="button"
					className={`chorus-toggle-btn ${params.enabled ? "active" : ""}`}
					onClick={() => onChange({ enabled: !params.enabled })}
					title={params.enabled ? "Disable chorus" : "Enable chorus"}
				>
					{params.enabled ? "On" : "Off"}
				</button>
			</div>

			<div className="chorus-modes">
				{CHORUS_MODES.map((mode) => (
					<button
						key={mode}
						type="button"
						className={`chorus-mode-btn ${params.mode === mode ? "active" : ""}`}
						onClick={() => handleModeChange(mode)}
						title={CHORUS_MODE_LABELS[mode]}
					>
						{mode}
					</button>
				))}
			</div>
		</div>
	);
}
